import { createEffect, ofType, Actions } from '@ngrx/effects';
import { filter, map, switchMap } from 'rxjs/operators';
import { zefWebsocketMessage } from '@zerops/zef/websocket';
import { extractBetween } from '@zerops/zef/core';
import { Action } from '@ngrx/store';

interface WebSocketMessage {
  type: string;
  data: any;
  subscriptionName: string;
  items?: any[];
  limit?: number;
  timeGroupBy?: string;
}


/**
 * Generic WebSocket update effect creator for time-series modules
 */
export function createTimeSeriesWebSocketUpdateEffect(
  config: {
    featureName: string;
    updateActionCreator: (data: any) => Action;
    parseMessage?: (message: WebSocketMessage, subscriptionName: string) => any;
  }
) {
  return (actions$: Actions) => createEffect(() =>
    actions$.pipe(
      ofType(zefWebsocketMessage),
      filter((action: any) => {
        const subscriptionName = action.message?.subscriptionName || '';
        return subscriptionName.includes(`${config.featureName}_`) && 
               (subscriptionName.includes('transaction-group') || subscriptionName.includes('resource-consumption-group'));
      }),
      map((action: any) => {
        let messageData: any;
        
        if (config.parseMessage) {
          messageData = config.parseMessage(action.message, action.message.subscriptionName);
        } else {
          // Default parsing logic using extractBetween (consistent with original transaction-debit implementation)
          const key = extractBetween('##', '##')(action.message.subscriptionName || '')[0] || '';
          
          messageData = {
            items: action.message.data?.update || action.message?.items || [],
            limit: action.message.data?.limit || action.message?.limit || 100,
            timeGroupBy: action.message.data?.timeGroupBy || action.message?.timeGroupBy || '1h',
            groupBy: action.message.data?.groupBy || extractGroupByFromSubscriptionName(action.message.subscriptionName || ''),
            key,
            projectId: action.message.data?.projectId,
            serviceId: action.message.data?.serviceId
          };
        }
        
        return config.updateActionCreator(messageData);
      })
    )
  );
}

/**
 * Extract groupBy from subscription name
 */
function extractGroupByFromSubscriptionName(subscriptionName: string): string {
  // Extract groupBy from subscription name pattern
  // e.g. "resource-consumption-base_resource-consumption-group_projectId_1h_##last24h##"
  const parts = subscriptionName.split('_');
  if (parts.length >= 3) {
    return parts[2]; // projectId, stackId, metricId
  }
  return 'projectId'; // default fallback
}

/**
 * Generic tracking effect creator for time-series subscriptions
 */
export function createTimeSeriesTrackingEffect(
  config: {
    groupActionType: any;
    trackActionCreator: (data: { data: any }) => Action;
    shouldTrack?: (action: any) => boolean;
  }
) {
  return (actions$: Actions) => createEffect(() =>
    actions$.pipe(
      ofType(config.groupActionType),
      filter((action: any) => {
        if (config.shouldTrack) {
          return config.shouldTrack(action);
        }
        // Default tracking logic - track if has current and not period update
        return !!action.data?.groupRange?.hasCurrent && !action.data.isPeriodUpdate;
      }),
      map((action: any) => config.trackActionCreator({ data: action }))
    )
  );
}

/**
 * Generic hourly update timer effect creator
 */
export function createTimeSeriesHourlyUpdateEffect(
  config: {
    hourlyTimer$: any;
    selectTrackedItems: any;
    groupActionCreator: (payload: any) => Action;
    shouldUpdate?: (item: any) => boolean;
  }
) {
  return (_store: any) => ({
    pipe: (hourlyTimer$: any) => hourlyTimer$.pipe(
      filter(([, trackedItems]: [any, any[]]) => trackedItems.length > 0),
      switchMap(([, trackedItems]: [any, any[]]) =>
        trackedItems
          .filter((item: any) => {
            if (config.shouldUpdate) {
              return config.shouldUpdate(item);
            }
            // Default update logic
            return item.data?.groupRange?.hasCurrent &&
                   (['1h', '1d'].includes(item.data.groupRange.timeGroupBy));
          })
          .map((item: any) => config.groupActionCreator({
            data: {
              ...item.data,
              isPeriodUpdate: true
            }
          }))
      )
    )
  });
}